'use strict';

/**
 * shared/provenance/provenance.js — THE ONE pure classifier that turns a content doc's
 * REAL publish-authority records into an HONEST byline + review claim (CS1).
 *
 * WHY THIS EXISTS (CS1 — honest bylines + review receipts)
 *   AInvest fakes "reviewed by <human>" with a disclosure paragraph. We PROVE it with the
 *   records we already keep — or we say nothing. This module is the single judgment core:
 *   given a doc, it derives (a) a machine-named PRODUCER lane and (b) a human REVIEW
 *   attribution that renders ONLY when a real human verdict record backs it. Two truthful
 *   grammars, nothing between them. A piece with no provable human verdict UNDER-claims
 *   (grammar B), even if a human did in fact look at it. Ambiguity always resolves to B.
 *
 * ⚠ THE AUTHOR-FIELD INTERLOCK (the exact dishonesty this unit kills)
 *   Auto-publish stamps `author = AUTO_PUBLISH_AUTHOR_ID` = the site owner's OWN uid. So this
 *   classifier NEVER reads `doc.author`. Producer + reviewer are derived from PIPELINE
 *   PROVENANCE — the content lane and the publish-transition audit trail — never from the
 *   author field. Rendering the author field as a byline would label machine content human.
 *
 * HUMAN-ASSERTER-ONLY (binding edge rule)
 *   `reviewedBy` renders ONLY from a HUMAN verdict record:
 *     1. a PublishService transition hop by a human-class actor (`admin:<uid>`) that APPROVED
 *        the piece (moved it to `published` or `scheduled` — the reviewer's approval act; the
 *        drip that later flips scheduled→published is a timer, not a verdict);
 *     2. a `gold_records` human-confirm verdict (by construction requires a human `by`),
 *        supplied by the caller as `opts.goldRecord`.
 *   Every OTHER transition actor is machine/automation and is NEVER review:
 *     auto-publish · auto-draft · drip · verifier · external-coverage · story-membership ·
 *     park:* · regenerate · spotlight:* · ops-cli:* (ops verdicts route through gold_records,
 *     never through the ops-cli transition tag) · editor-agent:* (EF future — an agent draft
 *     is a POINT, never a human ASSERT).
 *   An `admin:<uid>` whose uid is absent from the injected identity map (or resolves to a blank
 *   name) also UNDER-claims (we never leak a uid, and we never invent a name) — grammar B.
 *
 * IDENTITY → DISPLAY
 *   Human reviewers render by an INJECTED masthead identity (name + optional handle/url), keyed
 *   by the asserter uid. This module never owns or looks up that map — the caller resolves
 *   uid → identity and passes it in as `opts.identities`. The uid is a lookup KEY only — it is
 *   NEVER returned by this module and NEVER rendered on any public or AI surface.
 *
 * PURITY CONTRACT (house pattern of shared/publish/transitions.js)
 *   Requires nothing STATEFUL (no firebase, no env, no clock). Its ONE require is the sibling
 *   shared/provenance/masthead-identity.js, which is itself zero-require and pure — so this module
 *   stays referentially transparent, a classifiable pure test target, trivially browser-bundleable,
 *   and importable from the backend MCP `.mjs` runtime — ONE implementation, zero drift.
 */

// The ONE http(s)-only URL rule, imported (never re-implemented) so the value this module EMITS in
// `reviewedBy.url` is validated at the boundary that emits it — see identityForUid.
const { safeMastheadUrl } = require('./masthead-identity.js');

// ── Masthead identity is INJECTED, never owned here. The caller resolves uid → identity
//    server-side (src/lib/masthead-identity.ts) and passes it as `opts.identities`. This module
//    stays pure: no Firestore, no env, no clock. A uid absent from the map (or carrying a blank
//    name) UNDER-CLAIMS to grammar B — we never leak a uid and never invent a name.

// ── Machine-named producer lanes (public copy — COPY-VETO §1, VERBATIM). One vocabulary
//    everywhere: bylines, RSS dc:creator, AI routes, MCP.
const LANE_LABELS = Object.freeze({
  wire: 'Descout Desk · Wire', // breaking beats
  recaps: 'Descout Desk · Recaps', // ecosystem recaps
  articles: 'Descout Desk · Articles', // commissioned articles
  briefs: 'Descout Desk · Briefs', // syndicated desk briefs (CS2) — summarized, source-attributed
});

// ── Prose lane forms — used ONLY inside the GRAMMAR-A byline sentence (renderBylineParts).
//    The canonical `Descout Desk · X` labels above carry a middot; the grammar-A byline ALSO
//    joins its clauses with a middot, so embedding the label produced a middot COLLISION
//    ("Drafted by Descout Desk · Wire · Reviewed and approved by …" — two middots, ambiguous).
//    In the byline sentence the lane reads as prose instead; the canonical label is unchanged and
//    still used verbatim everywhere else (masthead, receipts producedBy, RSS dc:creator, AI, MCP).
//    Public copy — COPY-VETO §9 (approved-in-principle; exact strings await veto).
const LANE_PROSE = Object.freeze({
  wire: 'the Descout wire desk',
  recaps: 'the Descout recap desk',
  articles: 'the Descout article desk',
  briefs: 'the Descout brief desk',
});

// ── Grammar B (public copy, VERBATIM). HONEST BY OMISSION: it names what we can prove — the
//    desk drafted the piece — and stays SILENT on review rather than asserting a false one. The
//    old "Published automatically by the Descout desk under our editorial standard." line was
//    removed on 2026-07-24: it read as a claim we could not back, and its /policies#coverage link
//    was a policy dead-end. No link, no review clause, no apology.
const GRAMMAR_B_PREFIX = 'Drafted by ';

// ── The review CLAIM string. It lives HERE and only here (grep-guard, Global Constraint 3), and
//    is EXPORTED so the receipt page and the byline tests can compose with it instead of typing
//    the words out — a hardcoded claim at a render site is exactly what the guard exists to catch.
const REVIEW_CLAIM_PREFIX = 'Reviewed and approved by ';
// The masthead per-editor page copy (COPY-VETO §1).
const MASTHEAD_INTRO_SUFFIX = 'reviewed and approved.';
const MASTHEAD_BACKING_LINE = 'Every entry is backed by a signed publish record.';

// Transitions whose HUMAN authorship constitutes an approval-to-publish. Publishing OR
// scheduling a piece is the reviewer's approval; the drip that later executes a scheduled
// publish is a timer, not a verdict — so a `drip` publish hop never counts.
const APPROVAL_TO = Object.freeze(['published', 'scheduled']);

// gold_records decisions that constitute a human APPROVAL verdict (vs dismiss/reject/hold/route).
const APPROVAL_DECISIONS = Object.freeze(['confirm', 'override']);

/** A trimmed non-empty string, else null. */
function nonEmpty(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s.length ? s : null;
}

/**
 * Normalize an assortment of timestamp shapes to an ISO string, else null.
 * Handles: ISO string, Date, Firestore Timestamp ({_seconds}/{seconds} or .toDate()),
 * epoch millis. Never throws.
 */
function toIso(v) {
  if (v === null || v === undefined) return null;
  try {
    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return null;
      const t = Date.parse(s);
      return Number.isNaN(t) ? null : new Date(t).toISOString();
    }
    if (typeof v === 'number') return new Date(v).toISOString();
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString();
    if (typeof v.toDate === 'function') {
      const d = v.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
    }
    const secs = v._seconds !== undefined ? v._seconds : v.seconds;
    if (typeof secs === 'number') return new Date(secs * 1000).toISOString();
  } catch (_) {
    /* fall through */
  }
  return null;
}

/**
 * The uid IFF `by` is a HUMAN admin-class transition actor (`admin:<uid>`), else null.
 * The ONLY human-class transition prefix — every automation/agent actor is excluded by
 * construction (auto-publish, drip, verifier, ops-cli:*, editor-agent:*, spotlight:*, …).
 */
function humanUidFromTransitionActor(by) {
  const s = nonEmpty(by);
  if (!s) return null;
  const m = /^admin:(.+)$/.exec(s);
  if (!m) return null;
  return nonEmpty(m[1]);
}

/**
 * The masthead identity for a uid from the INJECTED map, or null.
 * Requires a non-empty `name`; `handle`/`url` are optional and normalize to null.
 * A uid with no entry, or an entry with a blank name, yields null → grammar B (fail-closed).
 *
 * ★ THE URL IS RE-VALIDATED HERE ★ — `url` is emitted into `reviewedBy.url`, which lands directly
 * in an `href` (DeskByline, MastheadEditorIdentity, the receipt page). `pickMastheadIdentity`
 * already applies the http(s) rule when it builds a map FROM user docs, but the map is INJECTED:
 * any caller may hand-build one (backend/scripts/audit-grammar-b-backfill-2026-07-24.js does), and
 * a future one may build it from somewhere unvalidated. So the guarantee sits at the boundary that
 * EMITS the value, not only at the boundary that happened to build the map — a `javascript:` url
 * normalizes to null here, no matter who supplied it.
 */
function identityForUid(uid, identities) {
  const u = nonEmpty(uid);
  if (!u || !identities || typeof identities !== 'object') return null;
  const raw = identities[u];
  if (!raw || typeof raw !== 'object') return null;
  const name = nonEmpty(raw.name);
  if (!name) return null;
  return { name, handle: nonEmpty(raw.handle), url: safeMastheadUrl(raw.url) };
}

/**
 * Every candidate publish/approval hop for the doc, newest-last-ish. Draws from BOTH:
 *   - `transitionLog[]` (bounded FIFO audit hops written by applyTransition/transitionAudit —
 *     the authoritative source for admin-UI reviews; each hop carries {to, by, at});
 *   - a synthetic hop from the top-level {transition, transitionBy, transitionAt} (covers the
 *     creation-stamp path — stampCreation writes those three but NO log hop, e.g. a recap
 *     materialized as an article by a human publishRecap).
 * The author field is NEVER read here.
 */
function collectHops(doc) {
  const hops = [];
  const log = doc && Array.isArray(doc.transitionLog) ? doc.transitionLog : [];
  for (const h of log) {
    if (h && typeof h === 'object') {
      hops.push({ to: nonEmpty(h.to), by: nonEmpty(h.by), at: toIso(h.at) });
    }
  }
  // Synthetic top-level hop. `transition` is `${from}→${to}`; parse the destination.
  const transition = nonEmpty(doc && doc.transition);
  if (transition) {
    const arrowIdx = transition.indexOf('→');
    const to = arrowIdx >= 0 ? nonEmpty(transition.slice(arrowIdx + 1)) : null;
    if (to) {
      hops.push({ to, by: nonEmpty(doc.transitionBy), at: toIso(doc.transitionAt) });
    }
  }
  return hops;
}

/**
 * The human REVIEW attribution for a doc, or null (under-claim). Order:
 *   1. the LATEST approval hop (to ∈ {published, scheduled}) by a human-class actor whose uid
 *      resolves to an identity in the INJECTED `identities` map;
 *   2. else a supplied `goldRecord` human-approval verdict (confirm/override) whose `by` resolves
 *      the same way.
 * Unknown/ambiguous → null.
 */
function deriveReviewed(doc, goldRecord, identities) {
  const hops = collectHops(doc);
  let best = null; // {identity, at, atMs}
  for (const h of hops) {
    if (!APPROVAL_TO.includes(h.to)) continue;
    const uid = humanUidFromTransitionActor(h.by);
    if (!uid) continue;
    const identity = identityForUid(uid, identities);
    if (!identity) continue; // human but unresolved → never leak a uid, never invent a name
    const atMs = h.at ? Date.parse(h.at) : NaN;
    if (!best || (Number.isFinite(atMs) && (!Number.isFinite(best.atMs) || atMs >= best.atMs))) {
      best = { identity, at: h.at, atMs };
    }
  }
  if (best) return { ...best.identity, at: best.at };

  if (goldRecord && typeof goldRecord === 'object') {
    if (APPROVAL_DECISIONS.includes(goldRecord.decision)) {
      const identity = identityForUid(goldRecord.by, identities);
      if (identity) return { ...identity, at: toIso(goldRecord.ts) };
    }
  }
  return null;
}

/**
 * The producer lane key for a doc.
 * @param {object} doc
 * @param {'article'|'breaking'|'recap'} kind  the caller's content-kind hint.
 */
function deriveLaneKey(doc, kind) {
  if (kind === 'breaking') return 'wire';
  if (kind === 'recap') return 'recaps';
  // Desk brief (CS2): a syndicated outlet summary materialized in the ARTICLES collection,
  // stamped `format: 'brief'`. We key off `format` — the dedicated content-format discriminator
  // — and NOT the doc's `producedBy: 'desk-brief'` string: `producedBy` is the NAME of this
  // classifier's own derived OUTPUT object ({key,label}), so keying off it invites a collision if
  // a derived provenance ever round-trips back through here. `format === 'brief'` is set ONLY on
  // real briefs, so this predicate is true for briefs and can never misfire on a commissioned
  // article (whose format is never 'brief'). Checked before the recap heuristic; briefs never
  // carry the ecosystem-recap markers, so order is immaterial to correctness.
  if (doc && doc.format === 'brief') return 'briefs';
  // article (or unknown): a recap materialized as an article is stamped category
  // 'ecosystem-recap' + sourceRecapId (EcosystemRecapService.publishRecap createArticle).
  if (doc && (doc.category === 'ecosystem-recap' || nonEmpty(doc.sourceRecapId))) return 'recaps';
  return 'articles';
}

/**
 * deriveProvenance — the CS1 core. PURE. Reads ONLY pipeline provenance (lane + transition
 * trail); NEVER the author field.
 *
 * @param {object} doc  a content doc (article | breaking_news | recap). Fields read:
 *   `transitionLog[]`, `transition`, `transitionBy`, `transitionAt`, `category`, `sourceRecapId`,
 *   `format` (CS2 desk-brief lane discriminator).
 * @param {object} [opts]
 * @param {'article'|'breaking'|'recap'} [opts.kind='article'] content-kind hint (lane).
 * @param {object} [opts.goldRecord] optional matching gold_records verdict {decision, by, ts}.
 * @param {Object<string,{name:string,handle?:(string|null),url?:(string|null)}>} [opts.identities]
 *   INJECTED masthead identity map (uid → identity), resolved by the caller. Absent → grammar B.
 * @returns {{producedBy:{key:string,label:string}, reviewedBy:({name:string,handle:string|null,url:string|null,at:string|null}|null), grammar:('A'|'B')}}
 */
function deriveProvenance(doc, opts = {}) {
  const kind = opts.kind || 'article';
  const laneKey = deriveLaneKey(doc, kind);
  const producedBy = { key: laneKey, label: LANE_LABELS[laneKey] };
  const reviewedBy = deriveReviewed(doc, opts.goldRecord, opts.identities);
  return { producedBy, reviewedBy, grammar: reviewedBy ? 'A' : 'B' };
}

/**
 * renderBylineParts — the render-ready parts for a byline block.
 *
 * Grammar A returns STRUCTURED parts because the handle is a LINK (to the reviewer's
 * mastheadUrl) — a skeptic clicks it and lands on a real, reputation-bearing account. When the
 * identity has no handle/url the renderer simply omits them. Grammar B is a flat string.
 * VERBATIM public copy.
 */
function renderBylineParts(prov) {
  // `producedBy` is guarded (not assumed present) so a hand-built or deserialized Provenance —
  // e.g. `{ grammar: 'B' }` crossing a server/client boundary — degrades to the generic desk
  // prose instead of throwing. `deriveProvenance` always sets `producedBy`; this guard is for
  // every OTHER caller of this function.
  const producedBy = prov && prov.producedBy;
  const laneProse = (producedBy && LANE_PROSE[producedBy.key]) || (producedBy && producedBy.label) || 'the Descout desk';
  if (prov && prov.grammar === 'A' && prov.reviewedBy) {
    // Prose lane form (not the middot label) to avoid a middot collision with the clause joiner.
    return {
      grammar: 'A',
      prefix: `${GRAMMAR_B_PREFIX}${laneProse} · ${REVIEW_CLAIM_PREFIX}`,
      name: prov.reviewedBy.name,
      handle: prov.reviewedBy.handle,
      handleUrl: prov.reviewedBy.url,
    };
  }
  return { grammar: 'B', text: `${GRAMMAR_B_PREFIX}${laneProse}` };
}

/**
 * toPublicProvenance — the machine-readable projection for AI routes / RSS / MCP payloads.
 * Lane label + review name/handle/url/at ONLY. NEVER a uid/email.
 *
 * An unreviewed piece gets an EXPLICIT `reviewedBy: null` — the key is PRESENT and null. The JSON
 * consumers pass it straight through (src/app/api/ai/{news,breaking}/route.ts and the MCP payload
 * in backend/src/mcp/lib/data-fetchers.mjs all emit `reviewedBy: provenance.reviewedBy`), so a
 * client can tell "we looked and there is no proven review" from a payload that predates the field.
 * Two surfaces deliberately signal the same thing by ABSENCE instead, because their medium has no
 * null: the RSS feed emits no <reviewedBy> element, and the receipt payload omits the key entirely
 * (buildReceipt, src/lib/receipts.ts — absence is its documented "no proof" signal).
 */
function toPublicProvenance(prov) {
  return {
    producedBy: prov.producedBy.label,
    reviewedBy: prov.reviewedBy
      ? {
          name: prov.reviewedBy.name,
          handle: prov.reviewedBy.handle,
          url: prov.reviewedBy.url,
          at: prov.reviewedBy.at,
        }
      : null,
  };
}

/** The masthead intro line for a per-editor reviewed-by page (COPY-VETO §1). */
function mastheadIntro(name) {
  return `Pieces ${name} ${MASTHEAD_INTRO_SUFFIX}`;
}

module.exports = {
  LANE_LABELS,
  LANE_PROSE,
  GRAMMAR_B_PREFIX,
  REVIEW_CLAIM_PREFIX,
  MASTHEAD_BACKING_LINE,
  APPROVAL_TO,
  APPROVAL_DECISIONS,
  deriveProvenance,
  deriveLaneKey,
  deriveReviewed,
  renderBylineParts,
  toPublicProvenance,
  identityForUid,
  humanUidFromTransitionActor,
  mastheadIntro,
  toIso,
};
